import React from 'react'
import _ from 'lodash'
import {FormControl, MenuItem, Select, Box, Chip, Checkbox, Button, InputLabel} from '@mui/material'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import CloseIcon from '@mui/icons-material/Close'

interface FormikMultiSelectProps {
    id: string
    className?: string
    error?: boolean
    isBasicMultiSelectChip?: boolean
    isLabel?: boolean
    labelText?: string
    helperText?: string
    formik: any
    options: unknown[]
    blurHandler?(...args: unknown[]): unknown
    chip?: boolean
    disableItems?: unknown[]
    maxLength?: any
    isMaxWidthEnabled?: boolean
    fullWidth?: boolean
}

const FormikMultiSelect = ({
    id,
    formik,
    options,
    className,
    error,
    chip,
    helperText,
    blurHandler,
    disableItems,
    fullWidth,
    isBasicMultiSelectChip,
    maxLength,
    isLabel,
    labelText,
    isMaxWidthEnabled
}: FormikMultiSelectProps) => {
    const [list, setList] = React.useState<any>([])
    const menuProps = {
        style: isMaxWidthEnabled ? {maxWidth: 'fit-content'} : {},
        PaperProps: {
            style: {
                maxHeight: 300
            }
        }
    }
    const handler = (event) => {
        if (maxLength && event.target.value.length > maxLength && isBasicMultiSelectChip) {
            return false
        }
        let temp: string[] = []
        if (event.target.value.indexOf('ALL') > -1) {
            if (formik.values[id].length !== list.length) {
                list.forEach((element) => {
                    const disableItemsTemp = disableItems ?? []
                    if (!disableItemsTemp.includes(element.name)) {
                        temp.push(element.name)
                    }
                })
            } else {
                temp = []
            }
            event.target.value = temp
        } else if (event.target.value.indexOf('None') > -1) {
            event.target.value = []
        }
        formik.handleChange(event)
    }
    const onDelete = (value) => {
        const fieldData = formik.getFieldProps(id)?.value
        formik.setFieldValue(
            id,
            fieldData?.filter((val) => val !== value)
        )
    }
    const onBlurHandler = () => {
        formik.setFieldTouched(id)
        if (blurHandler) blurHandler()
    }

    const getChips = (selected) => {
        if (selected.length === list.length && !isBasicMultiSelectChip) {
            return <Chip key='ALL' label='ALL' />
        } else {
            if (isBasicMultiSelectChip) {
                return selected.map((value) => (
                    <Chip
                        key={value}
                        data-testid='delete'
                        onDelete={() => onDelete(value)}
                        clickable
                        onMouseDown={(event) => {
                            event.stopPropagation()
                        }}
                        deleteIcon={<CloseIcon />}
                        label={value}
                        disabled={disableItems?.includes(value)}
                    />
                ))
            }
            return selected.map((value) => <Chip key={value} label={value} />)
        }
    }

    React.useEffect(() => {
        const tempList = _.cloneDeep(options)
        setList(tempList.sort((a: any, b: any) => a.displayName.localeCompare(b.displayName)))
    }, [options])

    React.useEffect(() => {
        if (formik.values[id].includes('ALL') && !isBasicMultiSelectChip && list.length > 0) {
            formik.setFieldValue(
                id,
                list.map((item) => item.name)
            )
        }
    }, [list])

    return (
        <FormControl>
            {isLabel && <InputLabel>{labelText}</InputLabel>}
            <Select
                className={className}
                labelId={`${id}-label`}
                error={formik.touched[id] && helperText && helperText !== null && helperText.length > 0}
                variant='outlined'
                name={id}
                id={id}
                fullWidth={fullWidth}
                value={formik.values[id]}
                onChange={handler}
                onBlur={onBlurHandler}
                multiple
                renderValue={(selected) => {
                    if (chip) {
                        return <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>{getChips(selected)}</Box>
                    } else {
                        return selected?.join(', ')
                    }
                }}
                MenuProps={menuProps}
            >
                {!isBasicMultiSelectChip && (
                    <Button
                        key='ALL'
                        value={
                            disableItems && formik.values[id].length === list.length - disableItems.length
                                ? 'None'
                                : 'ALL'
                        }
                        className='formik__multi-select__button'
                        disabled={disableItems && disableItems.length === list.length}
                    >
                        {disableItems && formik.values[id].length === list.length - disableItems.length
                            ? 'Select None'
                            : 'Select All'}
                    </Button>
                )}
                {list.map((opt) => (
                    <MenuItem
                        key={opt.name}
                        value={opt.name}
                        className='formik-multiselect__item'
                        data-testid={opt.name}
                        disabled={disableItems?.includes(opt.name)}
                        sx={{whiteSpace: 'normal', wordWrap: 'break-word'}}
                    >
                        {!isBasicMultiSelectChip && (
                            <Checkbox
                                checked={
                                    formik.values[id].indexOf(opt.name) > -1 || formik.values[id].length === list.length
                                }
                            />
                        )}
                        {isBasicMultiSelectChip ? (
                            <>
                                {opt.name}:&nbsp;{opt.displayName}
                            </>
                        ) : (
                            <>
                                {opt.displayName}&nbsp;&#40;{opt.name}&#41;
                            </>
                        )}
                    </MenuItem>
                ))}
            </Select>
            {helperText && formik.touched[id] && helperText !== null && helperText.length > 0 && (
                <p className='formik___error-text'>
                    <ErrorOutlineOutlinedIcon className='formik-error__info-icon' />
                    {helperText}
                </p>
            )}
        </FormControl>
    )
}

FormikMultiSelect.defaultProps = {
    blurHandler: () => {},
    disableItems: [],
    helperText: '',
    isBasicMultiSelectChip: false,
    chip: true,
    isLabel: false,
    labelText: '',
    maxLength: null,
    isMaxWidthEnabled: false,
    fullWidth: false
}

export default FormikMultiSelect
